import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Chip,
  ListItemText
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch } from "react-redux";
import { getDocumentTypeData, uploadDocuments, deleteDocument } from "../../../store/actions/documentAction";
import DocumentPreview from "../../../components/DocumentPreview";
import FileUploadDropzone from "../../../components/FileUploadDropzone";
import ReindexModal from "../../Documents/ReIndexModal";
import SplitModal from "../../Documents/SplitModal";
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog'
import SendEmailModal from '../../Documents/DocumentSearch/ResultsPanel/SendEmailModal';
import PermissionWrapper from "../../Setting/PermissionWrapper";

const buildFormData = (fileArr, selectedDocType, controlNo) => {
  const rows = Array.from(fileArr).map((file) => ({
    id: selectedDocType.id,
    name: file.name,
    docTypeCode: selectedDocType.document_type_code,
    type: file.type,
    file: file.file
  }));

  const formData = new FormData();
  rows.forEach((doc, index) => {
    formData.append(`documents[${index}][id]`, doc.id);
    formData.append(`documents[${index}][controlNo]`, controlNo);
    formData.append(`documents[${index}][docTypeCode]`, doc.docTypeCode);
    formData.append(`documents[${index}][docTypeId]`, doc.id);
    formData.append(`documents[${index}][files]`, doc.file);
  });
  return formData;
};

export default function OrderDocumentsSection(props) {
  const dispatch = useDispatch();
  const { existingDocuments, controlNumber, locationNumber, snackBarHandler, onRefreshDocuments,category } = props;
  const [selectedType, setSelectedType] = useState(null);
  const [newUploads, setNewUploads] = useState([]);
  const [preview, setPreview] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [reindexOpen, setReindexOpen] = useState(false);
  const [reindexDoc, setReindexDoc] = useState(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitDoc, setSplitDoc] = useState(null);
  const [confirmationAlert, setConfirmationAlert] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [openSendMailModal, setOpenSendMailModal] = useState(false);


  useEffect(() => {
    dispatch(getDocumentTypeData()).then(res => {
      setDocumentTypes(res?.data || []);
    });
  }, []);

  const handleSaveDocuments = async () => {
    if (!newUploads.length || !selectedType) return;

    const formData = buildFormData(newUploads, selectedType, controlNumber);

    snackBarHandler({
      status: true,
      type: 'info',
      message: 'Uploading documents to the server...'
    });

    uploadDocuments(locationNumber, formData).then(async (res) => {
      if (res?.status === 'success') {
        setNewUploads([]);
        setSelectedType(null);
        await onRefreshDocuments();
        snackBarHandler({
          status: true,
          type: 'success',
          message: 'Documents have been uploaded successfully!'
        });
      } else {
        snackBarHandler({
          status: true,
          type: 'error',
          message: 'Failed to upload documents. Please try again.'
        });
      }
    }).catch((err) => {
      console.error(err);
      snackBarHandler({
        status: true,
        type: 'error',
        message: 'An error occurred while uploading documents.'
      });
    });
  };

  const handleDeleteDocument = async () => {
    if (!docToDelete) return;
    snackBarHandler({
      status: true,
      type: 'info',
      message: 'Deleting document...'
    });
    setConfirmationAlert(false);
    setDocToDelete(null);
    try {
      // Call the delete document API
      const response = await deleteDocument(locationNumber, docToDelete.docId);
      if (response?.status === 'success') {
        snackBarHandler({
          status: true,
          type: 'success',
          message: 'Document deleted successfully!'
        });
        await onRefreshDocuments();
      } else {
        snackBarHandler({
          status: true,
          type: 'error',
          message: 'Failed to delete document. Please try again.'
        });
      }
    } catch (error) {
      console.error(error);
      snackBarHandler({
        status: true,
        type: 'error',
        message: 'An error occurred while deleting the document.'
      });
    }
  };

  const sortedDocuments = React.useMemo(() => {
    return [...existingDocuments].sort((a, b) => {
      if (!a.docModifiedDate) return 1;
      if (!b.docModifiedDate) return -1;

      return (
        new Date(b.docModifiedDate).getTime() -
        new Date(a.docModifiedDate).getTime()
      );
    });
  }, [existingDocuments]);

  return (
    <Box mt={2}>

      <Grid container spacing={2}>
        {/* LEFT: Upload new documents (15%) */}
         <PermissionWrapper category={category} level="READ_WRITE">
        <Grid item xs={12} md={2}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1">Upload Documents</Typography>

            <Autocomplete
              options={documentTypes}
              value={selectedType}
              onChange={(e, v) => setSelectedType(v)}
              getOptionLabel={(o) => o?.abbrevation || ''}
              isOptionEqualToValue={(option, value) => option.id == value.id}
              renderInput={(params) => (
                <TextField {...params} label="Document Type" />
              )}
            />

            <FileUploadDropzone
              disabled={!selectedType}
              files={newUploads}
              onFilesChange={(res) => { setNewUploads(res); /*setSelectedType(null);*/ }}
              onPreview={(file) => setPreview(file)}
              supportedTypes={[
                "application/pdf",
                "image/png",
                "image/jpeg",
                "image/tiff"
              ]}
              maxFileSizeMB={5}
              helperText="Supported formats: PDF, PNG, JPG, TIFF (Max 5MB)"
            />
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              disabled={!newUploads.length}
              onClick={handleSaveDocuments}
            >
              Save Documents
            </Button>
          </Paper>
        </Grid>
        </PermissionWrapper>

        {/* RIGHT: Existing documents thumbnails (15%) */}
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {/* HEADER */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle1">
                Saved Documents
              </Typography>
               <PermissionWrapper category={category} level="READ_WRITE">
              <SendEmailModal 
                open = {openSendMailModal}
                documents = {existingDocuments.map((e) => ({
                  ...e,
                  id: e.docId,
                  type: e.docAbbrevation,
                  control: controlNumber,
                  locationNumber: locationNumber,
                  filePath: e.preSignedFileURL
                }))}
                onClose = {() => setOpenSendMailModal(!openSendMailModal)}
                onSuccess = {(response) => {
                  if (response?.status === 'success') {
                      snackBarHandler({
                        status: true,
                        type: 'success',
                        message: 'Selected Documents sent successfully!'
                      });
                    } else {
                      snackBarHandler({
                        status: true,
                        type: 'error',
                        message: 'Failed to send documents. Please try again.'
                      });
                    }
                }}
              />
              <Button
                onClick={() => setOpenSendMailModal(true)}
              >Send mail</Button>
              <Chip
                label={existingDocuments.length}
                size="small"
                color="primary"
              />
              </PermissionWrapper>
            </Box>

            <Divider />

            {/* DOCUMENT LIST */}
            <List
              dense
              sx={{
                mt: 1,
                maxHeight: 400,
                overflow: "auto"
              }}
            >
              {sortedDocuments.map((doc, index) => (
                <React.Fragment key={index}>
                  <ListItemButton
                    selected={selectedDocId === doc}
                    onClick={() => {
                        setSelectedDocId(doc);
                        setPreview({
                          sourceData: doc,
                          url: doc.preSignedFileURL,
                          type: doc.originalFilename.split(".").pop()
                        })
                      }
                    }
                    sx={{
                      alignItems: "flex-start",
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#b3d4fc",
                      },

                      "&.Mui-selected:hover": {
                        backgroundColor: "#b3d4fc"
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {doc.originalFileNameAlias}
                        </Typography>
                      }
                      // secondary={
                      //   <Box mt={0.5}>
                      //     <Typography
                      //       variant="caption"
                      //       color="text.secondary"
                      //       display="block"
                      //     >
                      //       {doc.docAbbrevation}
                      //     </Typography>

                      //     <Typography
                      //       variant="caption"
                      //       color="text.secondary"
                      //     >
                      //       Last modified:{" "}
                      //       {doc.docModifiedDate
                      //         ? new Date(doc.docModifiedDate).toLocaleDateString()
                      //         : "—"}
                      //     </Typography>
                      //   </Box>
                      // }
                    />

                    {/* DELETE ICON */}
                    <PermissionWrapper category={category} level="READ_WRITE">
                    <IconButton
                      edge="end"
                      size="small"
                      title="Delete Document"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDocToDelete(doc);
                        setConfirmationAlert(true);
                      }}
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main"
                        }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    </PermissionWrapper>
                  </ListItemButton>

                  {index < existingDocuments.length - 1 && (
                    <Divider sx={{ ml: 2 }} />
                  )}
                </React.Fragment>
              ))}

              {/* EMPTY STATE */}
              {existingDocuments.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mt={2}
                >
                  No documents available
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* CENTER: Preview (70%) */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, minHeight: 500 }}>
            <ReindexModal
              open={reindexOpen}
              sourceDoc={{
                locationNumber: Number(locationNumber),
                controlNumber: Number(controlNumber),
                docAbbrevation: reindexDoc?.sourceData?.docAbbrevation || '',
                docTypeId: reindexDoc?.sourceData?.docTypeId || '',
                docTypeCode: reindexDoc?.sourceData?.docTypeCode || '',
                originalFilename: reindexDoc?.sourceData?.originalFilename || '',
                docTypeData: documentTypes
              }}
              onClose={() => {
                setReindexOpen(false);
                setReindexDoc(null);
                setPreview(null)
              }}
              onSave={async (success) => {
                setReindexOpen(false);
                setReindexDoc(null);
                setPreview(null)
                if (success) {
                  await onRefreshDocuments();
                }
              }}
            />
            <SplitModal
              open={splitOpen}
              sourceDoc={{
                locationNumber: Number(locationNumber),
                controlNumber: Number(controlNumber),
                docId: preview?.sourceData?.docId || null,
                type: preview?.type || '',
                url: preview?.url || '',
                docAbbrevation: preview?.sourceData?.docAbbrevation || '',
                docTypeId: preview?.sourceData?.docTypeId || '',
                docTypeCode: preview?.sourceData?.docTypeCode || '',
                originalFilename: preview?.sourceData?.originalFilename || '',
                docTypeData: documentTypes
              }}
              
              onClose={() => {
                setSplitOpen(false);
              }}
              onExtract={async (success, docData, originalFilename) => {
                if (success) {
                  setSplitOpen(false);
                  setReindexOpen(true);
                  setReindexDoc({
                    preview,
                    sourceData: {
                      ...(preview?.sourceData || {}),
                      ...(docData || {}),
                      originalFilename: originalFilename || preview?.sourceData?.originalFilename || ""
                    }
                  });
                }
              }}
              onDelete={async (success) => {
                if(success) {
                  setSplitOpen(false);
                  await onRefreshDocuments();
                }
              }}
            />

            <DocumentPreview
              preview={preview}
              height={800}
              onReindex={(doc) => {
                setReindexDoc(doc);
                setReindexOpen(true);
              }}
              onSplit={(doc) => {
                setSplitOpen(true);
                setSplitDoc(doc);
              }}
              showReindex={true}
              reindexing={reindexOpen ? true : false}
              showSplit={true}
              category={category}
            />
          </Paper>
        </Grid>
        <ConfirmationDialog
          isConfirmDialogOpen={confirmationAlert}
          title='Are you sure?'
          description="Pressing 'Yes' will delete selected item..."
          acceptTxt="Yes"
          rejectTxt="No"
          handleNo={() => setConfirmationAlert(false)}
          handleYes={handleDeleteDocument}
        />
      </Grid>
    </Box>
  );
}
